import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Form, Spinner, Alert, Button } from 'react-bootstrap';
import TitleWithBack from '../../components/common/TitleWithBack';
import PaginatedTable from '../../components/PaginatedTable';
import { isSeriesActive } from '../../utils/helpers';

function StudentsWorkDownload() {
  const [allModuleWork, setAllModuleWork] = useState([]); // Store all data
  const [filteredModuleWork, setFilteredModuleWork] = useState([]); // Store filtered data
  const [examSeriesOptions, setExamSeriesOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSeriesNames, setActiveSeriesNames] = useState([]); // Store all active series names

  const [filters, setFilters] = useState({
    examCentre: '',
    module: '',
    year: '',
    semester: '',
    examSeriesName: '' // Will be empty by default to show all active series
  });

  useEffect(() => {
    fetchExamSeries();
  }, []);

  useEffect(() => {
    fetchModuleWork();
  }, []); // Remove filters dependency to fetch all data once

  // Fetch active exam series
  const fetchExamSeries = async () => {
    try {
      const response = await axios.get('/api/exam-series', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const allSeries = response.data;
      setExamSeriesOptions(allSeries);

      // Find ALL active series for default filtering
      const activeSeries = allSeries.filter(series => isSeriesActive(series));

      if (activeSeries.length > 0) {
        const activeNames = activeSeries.map(series => series.name);
        setActiveSeriesNames(activeNames);
        // Don't set a specific filter - let the default logic show all active series
      }
    } catch (err) {
      console.error('Failed to fetch exam series', err);
    }
  };

  const fetchModuleWork = async () => {
    setLoading(true);
    try {
      // Fetch all data without filters
      const response = await axios.get('/api/archiver/reports/module-comprehensive-work', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setAllModuleWork(response.data);
      setLoading(false);
    } catch (err) {
      setError('Error fetching module work data.');
      setLoading(false);
    }
  };

  // Apply filters to the data
  useEffect(() => {
    let filtered = [...allModuleWork];

    // Filter by exam series name
    if (filters.examSeriesName) {
      // If a specific series is selected, filter by that series
      filtered = filtered.filter(work => 
        work.examSeries && work.examSeries.toLowerCase() === filters.examSeriesName.toLowerCase()
      );
    } else {
      // If no specific series is selected, show only active series by default
      if (activeSeriesNames.length > 0) {
        filtered = filtered.filter(work => 
          work.examSeries && activeSeriesNames.includes(work.examSeries)
        );
      }
      // If no active series found, show all data
    }

    // Filter by exam centre
    if (filters.examCentre) {
      filtered = filtered.filter(work => 
        work.examCentre && work.examCentre.toLowerCase().includes(filters.examCentre.toLowerCase())
      );
    }

    // Filter by module
    if (filters.module) {
      filtered = filtered.filter(work => 
        work.module && work.module.toLowerCase().includes(filters.module.toLowerCase())
      );
    }

    // Filter by year
    if (filters.year) {
      filtered = filtered.filter(work => 
        work.year && work.year.toString().includes(filters.year)
      );
    }

    // Filter by semester
    if (filters.semester) {
      filtered = filtered.filter(work => 
        work.semester && work.semester.toLowerCase().includes(filters.semester.toLowerCase())
      );
    }

    setFilteredModuleWork(filtered);
  }, [allModuleWork, filters, activeSeriesNames]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleDownload = async (assignmentId, examCentreId, documentName) => {
    try {
      const response = await axios.get(`/api/archiver/reports/module-comprehensive-work/download/${assignmentId}/${examCentreId}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', documentName || 'comprehensive_submission.zip');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError('Error downloading comprehensive ZIP file.');
    }
  };

  const columns = [
    { key: 'serial', header: '#' },
    { key: 'examCentre', header: 'Exam Centre' },
    { key: 'module', header: 'Module' },
    { key: 'moduleCode', header: 'Module Code' },
    { key: 'program', header: 'Program' },
    { key: 'year', header: 'Year' },
    { key: 'semester', header: 'Semester' },
    { key: 'examSeries', header: 'Exam Series' },
    { key: 'download', header: 'Download' },
  ];

  const renderRow = (work, index, pageIndex, pageSize) => (
    <>
      <td>{(pageIndex * pageSize) + index + 1}</td>
      <td>{work.examCentre}</td>
      <td>{work.module}</td>
      <td>{work.moduleCode}</td>
      <td>{work.program}</td>
      <td>{work.year}</td>
      <td>{work.semester}</td>
      <td>{work.examSeries || 'N/A'}</td>
      <td>
        {work.comprehensiveDocumentId && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleDownload(work.assignmentId, work.examCentreId, work.comprehensiveDocumentName)}
          >
            Download ZIP
          </Button>
        )}
      </td>
    </>
  );

  return (
    <Container className="mt-2">
      <TitleWithBack title="Student Work Download" />

      {/* Filters */}
      <Row className="mb-4">
        <Col md={2}>
          <Form.Group>
            <Form.Label>Exam Centre</Form.Label>
            <Form.Control
              type="text"
              name="examCentre"
              value={filters.examCentre}
              onChange={handleFilterChange}
              placeholder="Filter by exam centre"
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Module</Form.Label>
            <Form.Control
              type="text"
              name="module"
              value={filters.module}
              onChange={handleFilterChange}
              placeholder="Filter by module"
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Year</Form.Label>
            <Form.Control
              type="text"
              name="year"
              value={filters.year}
              onChange={handleFilterChange}
              placeholder="Filter by year"
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Semester</Form.Label>
            <Form.Control
              type="text"
              name="semester"
              value={filters.semester}
              onChange={handleFilterChange}
              placeholder="Filter by semester"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Exam Series</Form.Label>
            <Form.Select
              name="examSeriesName"
              value={filters.examSeriesName}
              onChange={handleFilterChange}
            >
              <option value="">All Active Series ({activeSeriesNames.length})</option>
              {examSeriesOptions.map(series => {
                const isActive = activeSeriesNames.includes(series.name);
                return (
                  <option key={series.id} value={series.name}>
                    {series.name} {isActive ? '(Active)' : ''}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Results */}
      {loading ? (
        <div className="text-center my-3">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : filteredModuleWork.length === 0 ? (
        <Alert variant="info">Nothing to show here</Alert>
      ) : (
        <PaginatedTable
          data={filteredModuleWork}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
    </Container>
  );
}

export default StudentsWorkDownload;